import { Box, Grid } from "@mui/material";

import { DashboardHighlight } from "../components/DashboardHighlight.tsx";
import { useLocalTargetState } from "../util/useLocalTargetState";
import { NextStepsBox } from "../components/NextStepsBox";
import { useLocalSolutionMapState } from "../util/useLocalSolutionMapState";
import { useLocalBaselineEmissionsState } from "../util/useLocalBaselineEmissionsState";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { analyzePlan } from "../util/planAnalysis/analyzePlan.ts";
import { LoadingIndicator } from "../components/LoadingIndicator.tsx";
import { EventCategory } from "@labs/shared/types/EventCategory.ts";
import PlanBySourceChart from "../charts/PlanBySourceChart.tsx";
import { LabsCard } from "../components/LabsCard.tsx";
import { RecommendationsList } from "../components/RecommendationsList.tsx";
import { useLocalUserState } from "../util/useLocalUserState.ts";

const ForecastLineChart = lazy(() => import("../charts/ForecastLineChart"));
const GapWaterfallChart = lazy(() => import("../charts/GapWaterfallChart"));

const DashboardOld = () => {
  const [target] = useLocalTargetState();
  const [solutionMap] = useLocalSolutionMapState();
  const [baselineEmissions] = useLocalBaselineEmissionsState();
  const [, setUser] = useLocalUserState();
  const [hoveredCharts] = useState(new Set<string>());
  const analyzedPlan = useMemo(
    () => analyzePlan(solutionMap, target, baselineEmissions),
    [baselineEmissions, solutionMap, target],
  );
  const hasPlan = analyzedPlan.planComponents.length > 0;

  // assume that if the user has landed on the dashboard, their "first pass" at
  // making a plan is complete
  useEffect(() => {
    setUser((prevUser) => ({ ...prevUser, firstPassComplete: true }));
  }, [setUser]);

  return (
    <Box>
      <Grid
        container
        spacing={4}
        sx={{
          alignItems: "stretch",
        }}
      >
        <Grid size={{ xs: 12, md: 4 }}>
          <LabsCard>
            <NextStepsBox
              target={target}
              baselineEmissions={baselineEmissions}
              analyzedPlan={analyzedPlan}
            />
          </LabsCard>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <LabsCard>
            <Suspense fallback={<LoadingIndicator />}>
              <GapWaterfallChart
                target={target}
                analyzedPlan={analyzedPlan}
                showEditLink
                hoveredCharts={hoveredCharts}
                analyticsCategory={EventCategory.Dashboard}
              />
            </Suspense>
          </LabsCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container spacing={4} sx={{ height: "100%" }}>
            <DashboardHighlight
              target={target}
              analyzedPlan={analyzedPlan}
              compactDisplay={false}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LabsCard>
            <Suspense fallback={<LoadingIndicator />}>
              <ForecastLineChart
                hoveredCharts={hoveredCharts}
                analyticsCategory={EventCategory.Dashboard}
                showEditLink
                analyzedPlan={analyzedPlan}
                target={target}
              />
            </Suspense>
          </LabsCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LabsCard>
            <Suspense fallback={<LoadingIndicator />}>
              <PlanBySourceChart
                analyzedPlan={analyzedPlan}
                showEditLink={false}
                hoveredCharts={hoveredCharts}
                analyticsCategory={EventCategory.Plan}
              />
            </Suspense>
          </LabsCard>
        </Grid>
        {hasPlan && (
          <Grid size={{ xs: 12, md: 6 }}>
            <RecommendationsList
              recommendations={analyzedPlan.recommendations}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DashboardOld;
